import { SolutionScenario } from '@/types/scenarios/solution';
import { Printer, Phone, Calendar, Wrench, HelpCircle, Package, FileText, BarChart3 } from 'lucide-react';

export const officeEquipment: SolutionScenario = {
  meta: {
    id: 'office-equipment',
    slug: 'office-equipment',
    name: 'Office Equipment',
    tagline: 'Genies that troubleshoot, schedule service, and order supplies',
    description: "A genie that troubleshoots copiers and printers, schedules service calls, and handles supplies and meter readings so your help desk only takes the calls that actually need a human.",
    market: 'Office Equipment',
    subMarkets: ['Managed Print Services', 'Copier Dealers', 'Office Technology', 'Document Solutions', 'IT Equipment'],
    specificNeeds: [
      'Equipment troubleshooting support',
      'Service and repair scheduling',
      'Toner and supplies ordering',
      'Meter reading collection',
      'Contract and billing inquiries',
      'New equipment consultations'
    ],
    commonRoles: ['Service Technicians', 'Account Managers', 'Help Desk Staff', 'Sales Representatives'],
    marketSize: '$85 billion global market',
    isFeatured: true
  },

  hero: {
    headline: 'Support that never takes a break',
    headlineGradient: 'Support that',
    headlineRest: 'never takes a break',
    subheadline: "A genie that troubleshoots copiers and printers, schedules service calls, and handles supplies and meter readings so your help desk only takes the calls that actually need a human.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'equipment-troubleshooting',
    'interactive-faq'
  ],

  applications: [
    {
      id: 'troubleshooting',
      name: 'Equipment Troubleshooting',
      description: 'Guide users through common issues before dispatching a technician',
      department: 'Support',
      icon: HelpCircle,
      playbookIds: ['equipment-troubleshooting', 'interactive-faq'],
      benefits: [
        'Resolve simple issues instantly',
        'Reduce unnecessary service calls',
        'Improve first-call resolution',
        'Empower end users'
      ]
    },
    {
      id: 'service-scheduling',
      name: 'Service Request Management',
      description: 'Schedule repairs and maintenance with intelligent technician routing',
      department: 'Service',
      icon: Wrench,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Priority-based scheduling',
        'Technician skill matching',
        'Automated confirmations',
        'Reduced response times'
      ]
    },
    {
      id: 'supplies-ordering',
      name: 'Supplies & Toner Orders',
      description: 'Automate toner and supply orders with proactive notifications',
      department: 'Operations',
      icon: Package,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Voice-activated ordering',
        'Automatic reorder reminders',
        'Contract-aware pricing',
        'Delivery tracking'
      ]
    },
    {
      id: 'sales-inquiries',
      name: 'Equipment Sales & Leasing',
      description: 'Capture leads for new equipment purchases and lease renewals',
      department: 'Sales',
      icon: Phone,
      playbookIds: ['lead-capture'],
      benefits: [
        '24/7 lead capture',
        'Needs assessment',
        'Demo scheduling',
        'Quote requests'
      ]
    },
    {
      id: 'contract-billing',
      name: 'Contract & Billing Support',
      description: 'Answer questions about service contracts, billing, and coverage details',
      department: 'Account Management',
      icon: FileText,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Coverage explanations',
        'Billing inquiry resolution',
        'Contract renewal reminders',
        'Usage report requests'
      ]
    },
    {
      id: 'meter-readings',
      name: 'Meter Reading Collection',
      description: 'Automate meter reading collection and usage tracking for managed services',
      department: 'Operations',
      icon: BarChart3,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Voice-based meter submission',
        'Automated collection reminders',
        'Usage tracking updates',
        'Billing accuracy improvement'
      ]
    }
  ],

  challenges: [
    {
      title: 'High Service Call Volume',
      description: 'Support teams overwhelmed with routine troubleshooting calls that could be self-resolved',
      impact: 'high',
      affectedAreas: ['Technician utilization', 'Response times', 'Customer satisfaction']
    },
    {
      title: 'After Hours Equipment Failures',
      description: 'Businesses need support when equipment fails outside normal hours',
      impact: 'high',
      affectedAreas: ['Customer productivity', 'Support costs', 'Competitive advantage']
    },
    {
      title: 'Supplies Management',
      description: 'Customers run out of toner or supplies, causing downtime and frustration',
      impact: 'medium',
      affectedAreas: ['Customer satisfaction', 'Revenue', 'Operational efficiency']
    },
    {
      title: 'Contract Complexity',
      description: 'Customers confused about coverage, billing, and service entitlements',
      impact: 'medium',
      affectedAreas: ['Customer frustration', 'Call duration', 'Support costs']
    }
  ],

  solutions: [
    {
      challenge: 'Overwhelming support call volume',
      solution: 'Voice Genie provides instant troubleshooting guidance',
      implementation: 'Deploy for common issues like paper jams, error codes, and connectivity problems',
      expectedResults: [
        '45% reduction in dispatched service calls',
        'Average issue resolution under 3 minutes',
        'Higher customer satisfaction scores'
      ]
    },
    {
      challenge: 'After hours equipment failures',
      solution: '24/7 Voice Genie support with smart escalation',
      implementation: 'Always on troubleshooting with priority service scheduling for critical issues',
      expectedResults: [
        'Round-the-clock customer support',
        '35% of issues resolved without dispatch',
        'Emergency service scheduling available'
      ]
    },
    {
      challenge: 'Manual supplies ordering process',
      solution: 'Voice-activated supplies ordering and proactive alerts',
      implementation: 'Connect to inventory systems for voice-based ordering and low-supply notifications',
      expectedResults: [
        '60% reduction in out-of-supply incidents',
        'Automated reorder workflows',
        'Improved contract compliance'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'e-automate',
        category: 'ERP',
        description: 'Office equipment dealer management system',
        required: false,
        popularityScore: 88
      },
      {
        name: 'PrintFleet',
        category: 'Fleet Management',
        description: 'Managed print services platform',
        required: false,
        popularityScore: 85
      },
      {
        name: 'FMAudit',
        category: 'Fleet Management',
        description: 'Device monitoring and meter collection',
        required: false,
        popularityScore: 82
      },
      {
        name: 'ConnectWise',
        category: 'Service Management',
        description: 'IT service management platform',
        required: false,
        popularityScore: 80
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Customer relationship management',
        required: false,
        popularityScore: 95
      },
      {
        name: 'ServiceNow',
        category: 'Service Management',
        description: 'Enterprise service management',
        required: false,
        popularityScore: 88
      }
    ]
  },

  metrics: {
    averageImplementationTime: '1-2 weeks',
    typicalROI: '320%',
    adoptionRate: '88% customer satisfaction',
    customerSatisfaction: '4.6/5'
  },

  demoConfig: {
    firstMessage: "Hello! I can help you troubleshoot your office equipment, schedule a service call, order supplies, or answer questions about your account. What can I assist you with today?",
    systemPrompt: "You are a knowledgeable office equipment support assistant. Help customers troubleshoot copiers, printers, and multifunction devices. Guide them through common issues, schedule service appointments, and assist with supplies orders. Be familiar with common error codes and resolution steps.",
    marketContext: "Office equipment dealer or managed print services provider handling support and service",
    exampleScenarios: [
      "My copier is showing error code SC-542",
      "I need to schedule a service call for our printer",
      "How do I order more toner?",
      "The printer is jamming constantly"
    ]
  },

  caseStudies: [
    {
      companyName: 'ProTech Office Solutions',
      companySize: '8 service locations',
      location: 'Southeast United States',
      challenge: 'Service team overwhelmed with basic troubleshooting calls, 40% of dispatches were for user-resolvable issues',
      implementation: 'Deployed Help Genie for front line support, troubleshooting, and service scheduling',
      timeline: '2 weeks',
      results: [
        {
          metric: 'Service Call Reduction',
          value: '42%',
          improvement: 'Fewer dispatches'
        },
        {
          metric: 'First-Call Resolution',
          value: '68%',
          improvement: 'Up from 23%'
        },
        {
          metric: 'Customer Wait Time',
          value: '< 30 sec',
          improvement: 'From 4+ minutes'
        },
        {
          metric: 'Technician Efficiency',
          value: '+35%',
          improvement: 'More meaningful visits'
        }
      ],
      quote: {
        text: "Our technicians now focus on complex repairs instead of showing customers how to clear paper jams. Help Genie handles the basics brilliantly, and customers love the instant support.",
        author: 'Jennifer Walsh',
        title: 'Service Director'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Office Equipment Voice Genie Guide',
      description: 'Implementing voice support for managed print and office technology',
      url: '/resources/office-equipment-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Reducing Service Calls with Voice Genies',
      description: 'How leading dealers cut costs while improving customer satisfaction',
      url: '/webinars/office-equipment-support'
    },
    {
      type: 'template',
      title: 'Equipment Troubleshooting Scripts',
      description: 'Pre-built voice scripts for common copier and printer issues',
      url: '/templates/equipment-troubleshooting'
    }
  ],

  faqs: [
    {
      question: 'Can the Genie troubleshoot specific error codes?',
      answer: 'Yes, Help Genie can be trained on your specific equipment models and error codes. It guides users through resolution steps for common issues like paper jams, toner replacement, and connectivity problems.'
    },
    {
      question: 'How does it handle supplies ordering?',
      answer: 'The Genie can connect to your ERP or ordering system to process supply requests. Customers simply say what they need, and the system handles the rest, including contract-aware pricing and delivery scheduling.'
    },
    {
      question: 'Does it integrate with fleet management tools?',
      answer: 'Yes, Help Genie integrates with popular platforms like PrintFleet, FMAudit, and e-automate for seamless meter collection, service ticket creation, and customer account management.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "The copier is down and\nyour client can't wait",
      subtitle: "See how your Genie handles real office equipment calls — resolving issues instantly, scheduling service, and ordering supplies before anyone runs out.",
    },
    chatScenarios: [
      {
        id: 'error_code',
        title: 'Error Code Triage',
        messages: [
          { type: 'user' as const, text: "Our main copier is showing error code SC-542 and it's completely locked up. We have a big print job due today.", delay: 300 },
          { type: 'genie' as const, text: "I can help with that. SC-542 is typically a fuser unit issue. First, let's try a quick fix — can you power the machine completely off, wait 30 seconds, then turn it back on?", delay: 1200 },
          { type: 'user' as const, text: "Okay, trying that now... it came back but the error is still there", delay: 2500 },
          { type: 'genie' as const, text: "That tells me the fuser needs service attention. Since you have a critical print job, I'm going to schedule a priority service call. A technician can be there within 2 hours. What's your company name and address?", delay: 3400 },
          { type: 'user' as const, text: "Henderson Law Group, 450 Commerce Drive, suite 300", delay: 4600 },
          { type: 'genie' as const, text: "Got it. Priority service ticket created for Henderson Law Group. Technician ETA is within 2 hours. Is there a backup device you can use in the meantime?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Error code identified (fuser)', delay: 6500 },
          { id: '2', text: 'Remote troubleshooting attempted', delay: 6700 },
          { id: '3', text: 'Priority service ticket created', delay: 6900 },
          { id: '4', text: 'Technician dispatched (2hr ETA)', delay: 7100 },
          { id: '5', text: 'Customer downtime minimized', delay: 7300 },
        ],
      },
      {
        id: 'paper_jam',
        title: 'Paper Jam Resolution',
        messages: [
          { type: 'user' as const, text: "The printer keeps jamming in the same spot — tray 2. I've cleared it three times already today.", delay: 300 },
          { type: 'genie' as const, text: "Recurring jams in tray 2 usually mean one of two things: the paper guides aren't set correctly, or the pickup rollers need cleaning. Let's check the guides first — is the paper sitting flush and are the side guides snug against the stack?", delay: 1200 },
          { type: 'user' as const, text: "Let me look... the guides look fine but the paper feels kind of wavy", delay: 2500 },
          { type: 'genie' as const, text: "That's likely the issue — humidity can cause paper to curl, which causes repeated jams. Try replacing the paper with a fresh ream from a sealed package. That often solves it immediately.", delay: 3400 },
          { type: 'user' as const, text: "Oh wow, I just swapped it out and it's printing fine now!", delay: 4600 },
          { type: 'genie' as const, text: "Great to hear! Store your paper in a cool, dry area and you shouldn't see that issue again. Anything else I can help with?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Issue diagnosed remotely', delay: 6500 },
          { id: '2', text: 'Guided troubleshooting completed', delay: 6700 },
          { id: '3', text: 'Problem resolved without dispatch', delay: 6900 },
          { id: '4', text: 'Preventive advice provided', delay: 7100 },
          { id: '5', text: 'Service call avoided — cost saved', delay: 7300 },
        ],
      },
      {
        id: 'toner_order',
        title: 'Supplies Ordering',
        messages: [
          { type: 'user' as const, text: "We're running low on toner for our Ricoh MPC3504. Can you send some over?", delay: 300 },
          { type: 'genie' as const, text: "Of course! I see your account has a service contract that includes toner. Which colors do you need — all four, or specific ones?", delay: 1200 },
          { type: 'user' as const, text: "Black and cyan are running low. Magenta and yellow are fine for now.", delay: 2500 },
          { type: 'genie' as const, text: "Got it — one black and one cyan toner cartridge for the MPC3504. These are covered under your contract at no additional charge. Standard delivery is 1–2 business days.", delay: 3400 },
          { type: 'user' as const, text: "Perfect, ship to our main office. Thanks!", delay: 4600 },
          { type: 'genie' as const, text: "Order placed — black and cyan toner shipping to your main office. You'll receive a tracking notification via email. Need anything else?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Account and contract verified', delay: 6500 },
          { id: '2', text: 'Supplies identified by model', delay: 6700 },
          { id: '3', text: 'Contract pricing applied', delay: 6900 },
          { id: '4', text: 'Order placed and tracking sent', delay: 7100 },
          { id: '5', text: 'Zero downtime for customer', delay: 7300 },
        ],
      },
      {
        id: 'meter_reading',
        title: 'Meter Reading',
        messages: [
          { type: 'user' as const, text: "I got an email about submitting my meter reading. Where do I find the counters on this machine?", delay: 300 },
          { type: 'genie' as const, text: "Happy to walk you through it. On most copiers, press the 'User Tools' or 'Home' button, then look for 'Counter' or 'Meter Check.' What brand is your machine?", delay: 1200 },
          { type: 'user' as const, text: "It's a Konica Minolta", delay: 2500 },
          { type: 'genie' as const, text: "On your Konica, press the 'Utility/Counter' key on the control panel. You'll see your total count and color count. Just read me those two numbers when you have them.", delay: 3400 },
          { type: 'user' as const, text: "Total is 47,832 and color is 12,415", delay: 4600 },
          { type: 'genie' as const, text: "Got it — 47,832 total and 12,415 color. Your meter reading is submitted. You're all set until next quarter!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Customer guided to meter screen', delay: 6500 },
          { id: '2', text: 'Brand-specific instructions provided', delay: 6700 },
          { id: '3', text: 'Meter reading captured accurately', delay: 6900 },
          { id: '4', text: 'Billing records updated', delay: 7100 },
          { id: '5', text: 'No manual follow-up needed', delay: 7300 },
        ],
      },
      {
        id: 'new_equipment',
        title: 'Equipment Inquiry',
        messages: [
          { type: 'user' as const, text: "We're expanding to a new office and need a multifunction copier. Something that handles high volume — probably 10,000+ pages a month.", delay: 300 },
          { type: 'genie' as const, text: "Congratulations on the expansion! For 10K+ pages monthly, you'll want a machine rated for that duty cycle with good speed and finishing options. Do you need color, and will you need scanning and faxing?", delay: 1200 },
          { type: 'user' as const, text: "Color is a must. Scanning yes, fax no. We'd prefer a lease.", delay: 2500 },
          { type: 'genie' as const, text: "Great — we have several options that would fit. I can have our account representative put together a proposal with 2–3 options and lease terms. What's the best contact?", delay: 3400 },
          { type: 'user' as const, text: "Karen Wells, kwells@newoffice.com, 555-923-1100", delay: 4600 },
          { type: 'genie' as const, text: "Perfect. I've captured your requirements and Karen's info. Your rep will reach out within one business day with a customized proposal. Welcome to the new office!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Volume and feature needs captured', delay: 6500 },
          { id: '2', text: 'Lease preference noted', delay: 6700 },
          { id: '3', text: 'Decision-maker contact collected', delay: 6900 },
          { id: '4', text: 'Sales lead created in CRM', delay: 7100 },
          { id: '5', text: 'Rep follow-up scheduled', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Stop Equipment Downtime",
      subtitle: "When the copier goes down, productivity stops. Your Genie resolves issues instantly, dispatches techs when needed, and keeps your customers' offices running.",
    },
    useCaseCards: [
      {
        id: 'remote-troubleshooting',
        title: 'Instant Troubleshooting',
        description: "Paper jams, error codes, connectivity issues — your Genie walks users through fixes before you roll a truck.",
        bullets: [
          'Resolve 40%+ of calls without a dispatch',
          'Error code identification and guided fixes',
          'Brand-specific troubleshooting steps',
          'Reduce technician windshield time',
        ],
        image: '/solutions-pages/office-equipment/usecase-troubleshooting.webp',
      },
      {
        id: 'service-dispatch',
        title: 'Priority Service Dispatch',
        description: "When it can't be fixed remotely, your Genie creates a service ticket and dispatches the right technician — fast.",
        bullets: [
          'Skill-matched technician routing',
          'Priority-based scheduling',
          'SLA-aware response times',
          'Customer kept informed at every step',
        ],
        image: '/solutions-pages/office-equipment/usecase-service-dispatch.webp',
      },
      {
        id: 'supplies-automation',
        title: 'Supplies & Toner Ordering',
        description: "Customers call, say what they need, and toner ships. Contract pricing applied automatically — no manual orders needed.",
        bullets: [
          'Voice-activated supply ordering',
          'Contract-aware pricing and coverage',
          'Automatic reorder reminders',
          'Delivery tracking provided',
        ],
        image: '/solutions-pages/office-equipment/usecase-supplies-ordering.webp',
      },
      {
        id: 'equipment-sales',
        title: 'Equipment Sales & Leasing',
        description: "Capture leads for new equipment, upgrades, and lease renewals — even after hours when IT managers are doing research.",
        bullets: [
          'Needs assessment and volume qualification',
          'Feature and budget capture',
          'Demo and proposal scheduling',
          'Competitive upgrade identification',
        ],
        image: '/solutions-pages/office-equipment/usecase-equipment-sales.webp',
      },
    ],
    finalCTA: {
      title: "Your Help Desk Never Closes",
      subtitle: "When office equipment fails at 7 PM before a board meeting, your Genie is there — troubleshooting, dispatching, and keeping your customers productive.",
      pillars: [
        {
          title: 'Always\nResolving',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Error codes diagnosed and fixed in minutes — not hours waiting for a tech.',
        },
        {
          title: 'Always\nDelivering',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Toner ordered, service booked, and tickets created before you even check email.',
        },
        {
          title: 'Always\nSelling',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Equipment leads captured and qualified — even at 9 PM when IT managers research.',
        },
      ],
      backgroundImage: '/solutions-pages/office-equipment/cta-background.webp',
    },
  }
};
